import {
  AuthTokenSource,
  IAuthTokenFactory,
  IAuthTokenOptions,
  IAuthTokens,
  IAuthTokensFactory,
  IAuthTokenStore,
  ISerializedAuthTokens,
  ITokenDebugger,
} from './authTokenTypes';
import { ServerCookies } from '../../utils/serverCookies';
import { AuthTokens } from './authTokens';
import { TokenDebugger } from './TokenDebugger';
import { NoOpTokenDebugger } from './NoopTokenDebugger';

const ENABLE_DEBUGGER_ON_SERVER = false;

export class AuthTokensFactory implements IAuthTokensFactory {
  private readonly debugger: ITokenDebugger;
  private readonly noOpDebugger: ITokenDebugger = new NoOpTokenDebugger();

  constructor(
    private readonly factories: Record<AuthTokenSource, IAuthTokenFactory>,
    options: IAuthTokenOptions = {},
  ) {
    this.debugger = options?.debugger || new TokenDebugger();
  }

  createOnServer(
    cookies: ServerCookies,
    options: IAuthTokenOptions = {},
  ): IAuthTokens {
    const tokenStores = Object.values(this.factories).reduce((acc, factory) => {
      const store = factory.createOnServer(cookies, {
        ...options,
        // Quick and dirty way to disable debugger on server to avoid noise in logs
        debugger: ENABLE_DEBUGGER_ON_SERVER ? this.debugger : this.noOpDebugger,
      });
      if (store) {
        acc.push(store);
      } else {
        acc.push(factory.createEmpty());
      }

      return acc;
    }, [] as IAuthTokenStore[]);

    return new AuthTokens(tokenStores, {
      ...options,
      debugger: ENABLE_DEBUGGER_ON_SERVER ? this.debugger : this.noOpDebugger,
    });
  }

  createOnClient(
    fromServer: ISerializedAuthTokens,
    options: IAuthTokenOptions = {},
  ): IAuthTokens {
    const results: IAuthTokenStore[] = [];
    for (const [source, factory] of Object.entries(this.factories)) {
      const token = fromServer[source];
      if (token) {
        const store = factory.createOnClient(token, {
          ...options,
          debugger: this.debugger,
        });
        results.push(store);
      } else {
        console.warn(
          `No token found for ${source} - all tokens must be provided when serializing`,
        );
        results.push(
          factory.createEmpty({
            ...options,
            debugger: this.debugger,
          }),
        );
      }
    }

    return new AuthTokens(results, options);
  }
}
